import styled, { createGlobalStyle } from "styled-components";
import { Modal } from "react-bootstrap";
import React, { useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner } from "ui";
import { Post, PostFormState } from "shared/types/post";

import { useDispatch, useSelector } from "react-redux";
import {
  closeBulkCreatePostModal,
  duplicatePostFormInBulkCreate,
  openCreatePostModalV2,
  savePostFormInBulkCreate,
} from "./bulk-create-post.actions";
import { EditPostModal } from "../components/CreatePostModal";
import { ReduxRootState } from "../../../types/redux-store";
import { getOrderedEntities } from "../../../selectors/selectorUtils";
import {
  BulkTabContext,
  ChildCallbacks,
  SaveFormResult,
} from "../../../components/Posts/PostForm/types";
import { checkInstagramPostOverlapping } from "../../../actions/postForm";
import { usePostForm } from "../../../components/Posts/PostForm/use-post-form";
import PostForm from "../../../components/Posts/PostForm/PostForm";
import { PostMiniCardTabs } from "../components/post-mini-card/post-mini-card-tabs";
import { formToMiniCardState } from "../components/post-mini-card/state-conversion";
import {
  getUser,
  getUserPostStatuses,
} from "../../../selectors/commonSelectors";
import { selectedPostIdSlice, useBulkState } from "./bulk-create-post.model";
import { StatePostSaveSuccess } from "../components/post-save-success/post-save-success";
import {
  closeAllWithConfirm,
  closeFormWithConfirm,
  useChildCallbacks,
  useHandleFormDirty,
} from "./hooks";
import { hotkeyManager } from "../../../ui/SkedCaption/hot-key-manager";
import { useTrackEvent } from "../../../libs/analytics";
import {
  CREATE_POST_BULK_ADD,
  CREATE_POST_BULK_CANCEL,
  CREATE_POST_BULK_DUPLICATE,
  CREATE_POST_LI,
  CREATE_POST_SINGLE_CANCEL,
  CREATE_POST_YT,
  CREATE_SINGLE_POST,
} from "../../../constants/Events";
import { getPostedState } from "../../../utils/posts";

export const BulkCreatePostModal = () => {
  const dispatch = useDispatch();
  const pendingState = useBulkState().bulkModalPendingState;
  const pendingJsx =
    pendingState.isLoading || pendingState.error ? (
      <>
        <Modal.Header closeButton={!!pendingState.error}>
          <Modal.Title>Edit Post</Modal.Title>
        </Modal.Header>
        <div className="w-[1224px] h-[748px] flex items-center justify-center relative">
          {pendingState.isLoading && (
            <LoadingSpinner noOverlay wrapperAbsolutePosition />
          )}
          {pendingState.error && (
            <div className="text-red-500">
              Error on Loading Posts, details: {pendingState.error}
            </div>
          )}
        </div>
      </>
    ) : undefined;
  return (
    <ModalContainer
      title="Bulk Create Posts"
      isLoading={false}
      isHidden={false}
      onCancel={() => {
        if (pendingState?.error) {
          dispatch(closeBulkCreatePostModal());
        }
      }}
    >
      {pendingJsx ? pendingJsx : <BulkTabs />}
    </ModalContainer>
  );
};

const BulkTabs = () => {
  const dispatch = useDispatch();
  const forms = useSelector((state: ReduxRootState) =>
    getOrderedEntities(state.forms.postForms)
  );
  const user = useSelector(getUser);
  const userCollabSettings = useSelector(getUserPostStatuses);
  const { selectedPostId, setSelectedPostId } = useSelectedPostId();
  const childrenCallbacksMapRef = useRef<Map<string, ChildCallbacks>>(
    new Map()
  );
  const trackEvent = useTrackEvent();

  hotkeyManager.useSubmitPostHotkey(() => {
    console.log(childrenCallbacksMapRef, selectedPostId);
    childrenCallbacksMapRef.current
      ?.get(selectedPostId || "")
      ?.onSubmit?.("save-and-show-success-screen");
  }, [selectedPostId]);

  const isCreation = forms.some((c) => c.requestType === "create");
  return (
    <div className="flex flex-row">
      <PostMiniCardTabs
        showAddNewButton={isCreation}
        cards={forms.map((form) =>
          formToMiniCardState({
            form,
            isSelected: selectedPostId === form.post._id,
            user,
            userCollabSettings,
          })
        )}
        onAddNewForm={() => {
          const selectedForm = forms.find(
            (form) => form.post._id === selectedPostId
          );
          dispatch(
            openCreatePostModalV2({
              accountIds: selectedForm?.accounts?.map((acc) => acc._id) || [],
            })
          );
          trackEvent({
            eventName: CREATE_POST_BULK_ADD,
          });
        }}
        onSelect={(card) => {
          setSelectedPostId(card.postId);
        }}
        onCloseAll={() => {
          closeAllWithConfirm({
            forms,
            dispatch,
            onConfirmCloseAll: () => {
              if (isCreation) {
                trackEvent({
                  eventName: CREATE_POST_BULK_CANCEL,
                  metadata: { length: forms.length },
                });
              }
            },
          });
        }}
        onClose={(card) => {
          closeFormWithConfirm({
            form: card.form,
            dispatch,
            onConfirmClose: () => {
              if (isCreation) {
                trackEvent({ eventName: CREATE_POST_SINGLE_CANCEL });
              }
            },
          });
        }}
        onDuplicate={(card) => {
          dispatch(
            duplicatePostFormInBulkCreate({ fromId: card.form.post._id })
          );
          toast.success("The post has been successfully duplicated.");
          trackEvent({ eventName: CREATE_POST_BULK_DUPLICATE });
        }}
      />
      {forms.map((form) => {
        const handleChildCallbackRefChange = (
          vl: ChildCallbacks | undefined
        ): void => {
          if (vl) {
            childrenCallbacksMapRef.current.set(form.post._id, vl);
          } else {
            childrenCallbacksMapRef.current.delete(form.post._id);
          }
        };

        const resolveTitle = () => {
          if (form.requestFormTitle) {
            return form.requestFormTitle;
          }

          if (form.requestType === "create") {
            return "Create Post";
          }
          const postedState = getPostedState(form.post);
          if (postedState === "success") {
            return "Post Summary";
          }
          if (postedState === "failed") {
            return "Post Again";
          }
          return "Edit Post";
        };

        return (
          <div
            key={form.requestedId}
            className={`flex-1 ${selectedPostId === form.post._id ? "" : "hidden"} m-[24px]`}
          >
            {form.formSaveData ? (
              <div className="min-h-[700px] flex items-center">
                <StatePostSaveSuccess form={form} />
              </div>
            ) : (
              <>
                <div className="text-[24px] font-bold mb-[16px] leading-[32px]">
                  {resolveTitle()}
                </div>

                {form.requestType === "create" && (
                  <CreatePost
                    form={form}
                    onChildCallbacksRefChange={handleChildCallbackRefChange}
                  />
                )}
                {form.requestType === "update" && (
                  <EditPost
                    form={form}
                    onChildCallbacksRefChange={handleChildCallbackRefChange}
                  />
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CreatePost = ({
  form,
  onChildCallbacksRefChange,
}: {
  form: PostFormState;
  onChildCallbacksRefChange: (vl: ChildCallbacks | undefined) => void;
}) => {
  const dispatch = useDispatch();
  const { handleOnFormValueChanged } = useHandleFormDirty({ dispatch });
  const childCallbacks = useChildCallbacks({ onChildCallbacksRefChange });
  const trackEvent = useTrackEvent();

  const trackEventYT = (post: Post) => {
    const isVideoPostWithYouTube =
      post.type === "video" &&
      post.accounts?.some((el) => el.platformType === "YT");

    if (isVideoPostWithYouTube && post.meta?.format?.duration) {
      // eslint-disable-next-line unicorn/require-number-to-fixed-digits-argument
      const duration = +(+post.meta.format.duration).toFixed();
      if (duration <= 60) {
        trackEvent({
          eventName: CREATE_POST_YT,
          metadata: { duration: "< 1 min" },
        });
      } else if (duration <= 900) {
        trackEvent({
          eventName: CREATE_POST_YT,
          metadata: { duration: "1 to 15 min" },
        });
      } else if (duration > 900) {
        trackEvent({
          eventName: CREATE_POST_YT,
          metadata: { duration: "over 15 min" },
        });
      }
    }
  };

  const trackEventLI = (post: Post) => {
    if (post._id === "NEW_POST") {
      // eslint-disable-next-line unicorn/no-array-for-each
      post.accounts?.forEach((account) => {
        if (account.platformType === "LI") {
          trackEvent({
            eventName: CREATE_POST_LI,
            metadata: {
              pageType: account.linkedinUrn?.includes("person")
                ? "Personal Page"
                : "Company Page",
            },
          });
        }
      });
    }
  };

  const bulkTab: BulkTabContext = {
    formId: form.post._id,
    childCallbacks,
  };
  const postForm = usePostForm(
    form.post,
    {
      async onSave(data: SaveFormResult, saveAction) {
        const { post: postForm } = data;
        await new Promise((resolve) =>
          dispatch(
            checkInstagramPostOverlapping({
              postId: postForm._id,
              accounts: postForm.accounts,
              isNow: postForm.isNow,
              when: postForm.when,
              done: resolve,
            })
          )
        );

        dispatch(
          savePostFormInBulkCreate({
            form: data,
            postSaveAction: saveAction || "save-and-close",
            requestType: form.requestType,
          })
        );

        trackEvent({ eventName: CREATE_SINGLE_POST });
        trackEventYT(postForm);
        trackEventLI(postForm);
      },
      onSuccess() {},
    },
    bulkTab
  );
  return (
    <PostForm
      {...postForm}
      setIsFormValueChanged={(isDirty: boolean) => {
        handleOnFormValueChanged(form.post._id, isDirty);
      }}
      onCancel={() => {
        closeFormWithConfirm({
          form,
          dispatch,
          onConfirmClose: () => {
            trackEvent({ eventName: CREATE_POST_SINGLE_CANCEL });
          },
        });
      }}
      accountEditingIsDisabled={false}
      bulkTab={bulkTab}
    />
  );
};

const EditPost = ({
  form,
  onChildCallbacksRefChange,
}: {
  form: PostFormState;
  onChildCallbacksRefChange: (vl: ChildCallbacks | undefined) => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { handleOnFormValueChanged } = useHandleFormDirty({ dispatch });
  const childCallbacks = useChildCallbacks({ onChildCallbacksRefChange });
  return (
    <EditPostModal
      post={form.post}
      accountEditingIsDisabled={!!form.accountSelectionDisabled}
      isHidden={false}
      bulkTab={{
        formId: form.post._id,
        onFormValueChanged: (isDirty) => {
          handleOnFormValueChanged(form.post._id, isDirty);
        },
        onSave: async (saveResult, saveAction) => {
          dispatch(
            savePostFormInBulkCreate({
              form: saveResult,
              postSaveAction: saveAction || "save-and-close",
              requestType: form.requestType,
            })
          );
        },
        onCancel: () => {
          closeFormWithConfirm({ form, dispatch });
        },
        childCallbacks,
      }}
    />
  );
};

const ModalContainer = (props: {
  title: string;
  isLoading: boolean;
  isHidden: boolean;
  onCancel: () => void;
  children: React.ReactNode;
}) => {
  return (
    <CreatePostModalRoot
      show={!props.isHidden}
      onHide={props.onCancel}
      animation={false}
      bsStyle="giselle"
      enforceFocus={false}
      autoFocus={false}
      keyboard={false}
    >
      <div data-testid="bulk-create-post-modal">
        <ToastContainer position="top-center" />
        <ChildrenModalFixScrollStyles />
        <Modal.Body>
          {props.isLoading && <LoadingSpinner />}
          {props.children}
        </Modal.Body>
      </div>
    </CreatePostModalRoot>
  );
};

const useSelectedPostId = () => {
  const dispatch = useDispatch();
  const id = useBulkState().selectedPostId;
  return {
    selectedPostId: id,
    setSelectedPostId: (postId: string | undefined) => {
      dispatch(selectedPostIdSlice.actions.set(postId));
    },
  };
};

const CreatePostModalRoot = styled(Modal)<{ bsStyle?: string }>`
  // fixes for cross angular/react modals
  overflow-y: auto;
  z-index: 1040 !important;

  .modal-giselle {
    width: auto !important;
    max-width: 1400px;

    .modal-body {
      padding-left: 0;
      padding-bottom: 0;
      margin-bottom: 0;
      padding-right: 0;
    }
  }
`;

const ChildrenModalFixScrollStyles = createGlobalStyle`
  .modal {
    overflow-y: auto;
  }
`;
